import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { PageBackground } from "@/components/ui/page-background";
import { AppHeader } from "@/components/app-header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Music, Users, Calendar, ArrowRight, Building, 
  PoundSterling, FileText, ChevronLeft, CalendarDays, BarChart 
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";

export default function BookingManagement() {
  const [, setLocation] = useLocation();
  const { user } = useAuth();

  // For god users, fetch all orgs with artist management
  // For regular users, filter their organisationUsers
  const [organisationsWithArtistManagement, setOrganisationsWithArtistManagement] = useState<any[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);

  useEffect(() => {
    async function fetchOrganisations() {
      if (!user) {
        setLoadingOrgs(false);
        return;
      }
      
      if (user.role === 'god') {
        // God user - fetch all organisations with artist management
        try {
          const response = await fetch('/api/organisations');
          if (response.ok) {
            const orgs = await response.json();
            const orgsWithAM = orgs
              .filter((org: any) => org.artistManagement === true)
              .map((org: any) => ({
                organisation: org,
                role: 'god' // God users have all permissions
              }));
            
            // If only one org, auto-navigate to its acts page
            if (orgsWithAM.length === 1) {
              setLocation(`/organisations/${orgsWithAM[0].organisation.id}/acts`);
              return;
            }
            
            setOrganisationsWithArtistManagement(orgsWithAM);
          }
        } catch (error) {
          console.error('Error fetching organisations:', error);
        }
      } else {
        // Regular user - filter their organisationUsers
        const orgsWithAM = user.organisationUsers?.filter(
          ou => ou.organisation.artistManagement === true
        ) || [];
        
        // If only one org, auto-navigate to its acts page
        if (orgsWithAM.length === 1) {
          setLocation(`/organisations/${orgsWithAM[0].organisation.id}/acts`);
          return;
        }
        
        setOrganisationsWithArtistManagement(orgsWithAM);
      }
      setLoadingOrgs(false);
    }
    
    fetchOrganisations();
  }, [user, setLocation]);

  // Fetch booking stats for each organisation
  const { data: bookingStats = {} } = useQuery({
    queryKey: ['booking-stats', organisationsWithArtistManagement.map(o => o.organisation.id)],
    queryFn: async () => {
      const stats: Record<number, any> = {};
      
      for (const ou of organisationsWithArtistManagement) {
        const [bookingsRes, actsRes] = await Promise.all([
          fetch(`/api/bookings?organisationId=${ou.organisation.id}`),
          fetch(`/api/acts?organisationId=${ou.organisation.id}`)
        ]);
        
        if (bookingsRes.ok && actsRes.ok) {
          const bookings = await bookingsRes.json();
          const acts = await actsRes.json();
          
          stats[ou.organisation.id] = {
            totalBookings: bookings.length,
            confirmedBookings: bookings.filter((b: any) => b.status === 'confirmed').length,
            pendingBookings: bookings.filter((b: any) => b.status === 'pending').length,
            totalActs: acts.length,
            activeActs: acts.filter((a: any) => a.active !== false).length,
            totalRevenue: bookings
              .filter((b: any) => b.status === 'confirmed' || b.status === 'completed')
              .reduce((sum: number, b: any) => sum + (b.fee || 0), 0)
          };
        }
      }
      
      return stats;
    },
    enabled: organisationsWithArtistManagement.length > 0 && !loadingOrgs
  });

  if (loadingOrgs) {
    return (
      <PageBackground>
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 200px)' }}>
            <div className="text-center">
              <Music className="h-8 w-8 animate-pulse mx-auto mb-4 text-slate-700" />
              <p className="text-slate-700">Loading organisations...</p>
            </div>
          </div>
        </main>
      </PageBackground>
    );
  }

  if (organisationsWithArtistManagement.length === 0) {
    return (
      <PageBackground>
        <AppHeader />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <Music className="h-16 w-16 mx-auto mb-4 text-slate-400" />
            <h2 className="text-2xl font-semibold text-slate-900 mb-2">
              No Artist Management Access
            </h2>
            <p className="text-slate-600 mb-6">
              You don't have artist management permissions for any organisations.
            </p>
            <Button 
              onClick={() => setLocation('/management-selection')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </main>
      </PageBackground>
    );
  }

  return (
    <PageBackground>
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="outline"
              onClick={() => setLocation('/management-selection')}
              className="bg-white/40 backdrop-blur-sm border-white/30 hover:bg-white/50"
            >
              <ChevronLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900 drop-shadow-sm">
                Booking Management
              </h1>
              <p className="text-slate-600 mt-1">
                Select an organisation to manage artists and bookings
              </p>
            </div>
          </div>
        </div>

        {/* Organisation Cards */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {organisationsWithArtistManagement.map((ou) => {
            const stats = bookingStats[ou.organisation.id] || {};
            const hasStats = Object.keys(stats).length > 0;
            
            return (
              <Card
                key={ou.organisation.id}
                className="bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30 hover:bg-white/30 transition-all duration-300 group hover:scale-[1.02] relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.1) 100%)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.4)',
                }}
              >
                {/* Specular highlight on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, transparent 50%, rgba(255,255,255,0.1) 100%)',
                  }}
                />
                
                <div className="p-6 relative z-10">
                  {/* Organisation Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-1">
                        {ou.organisation.name}
                      </h3>
                      <Badge variant="secondary" className="text-xs">
                        {ou.role}
                      </Badge>
                    </div>
                    <Building className="h-8 w-8 text-purple-600/50" />
                  </div>

                  {/* Stats Grid */}
                  {hasStats && (
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                        <div className="flex items-center gap-2 mb-1">
                          <Music className="h-3 w-3 text-purple-600" />
                          <span className="text-xs text-slate-600">Acts</span>
                        </div>
                        <div className="text-lg font-bold text-slate-900">
                          {stats.activeActs || 0}
                        </div>
                      </div>
                      
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="h-3 w-3 text-blue-600" />
                          <span className="text-xs text-slate-600">Bookings</span>
                        </div>
                        <div className="text-lg font-bold text-slate-900">
                          {stats.totalBookings || 0}
                        </div>
                      </div>
                      
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="h-3 w-3 text-amber-600" />
                          <span className="text-xs text-slate-600">Pending</span>
                        </div>
                        <div className="text-lg font-bold text-slate-900">
                          {stats.pendingBookings || 0}
                        </div>
                      </div>
                      
                      <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 border border-white/20">
                        <div className="flex items-center gap-2 mb-1">
                          <PoundSterling className="h-3 w-3 text-green-600" />
                          <span className="text-xs text-slate-600">Revenue</span>
                        </div>
                        <div className="text-lg font-bold text-slate-900">
                          £{((stats.totalRevenue || 0) / 100).toFixed(0)}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={() => setLocation(`/organisations/${ou.organisation.id}/acts`)}
                      className="w-full bg-purple-600 hover:bg-purple-700 text-white group"
                    >
                      <Music className="h-4 w-4 mr-2" />
                      Manage Acts & Bookings
                      <ArrowRight className="h-4 w-4 ml-auto group-hover:translate-x-1 transition-transform" />
                    </Button>
                    
                    <Button
                      onClick={() => setLocation(`/organisations/${ou.organisation.id}/bookings/calendar`)}
                      variant="outline"
                      className="w-full bg-white/20 hover:bg-white/30 border-white/30"
                    >
                      <CalendarDays className="h-4 w-4 mr-2" />
                      View Calendar
                    </Button>
                    
                    <Button
                      onClick={() => setLocation(`/booking-analytics/${ou.organisation.id}`)}
                      variant="outline"
                      className="w-full bg-white/20 hover:bg-white/30 border-white/30"
                    >
                      <BarChart className="h-4 w-4 mr-2" />
                      View Analytics
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Summary Stats */}
        {organisationsWithArtistManagement.length > 1 && (
          <div className="mt-8 p-6 bg-white/20 backdrop-blur-xl rounded-2xl border border-white/30">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">
              Total Across All Organisations
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {Object.values(bookingStats).reduce((sum, s: any) => sum + (s.activeActs || 0), 0)}
                </div>
                <div className="text-sm text-slate-600">Active Acts</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">
                  {Object.values(bookingStats).reduce((sum, s: any) => sum + (s.totalBookings || 0), 0)}
                </div>
                <div className="text-sm text-slate-600">Total Bookings</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-amber-600">
                  {Object.values(bookingStats).reduce((sum, s: any) => sum + (s.pendingBookings || 0), 0)}
                </div>
                <div className="text-sm text-slate-600">Pending</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600">
                  £{(Object.values(bookingStats).reduce((sum, s: any) => sum + (s.totalRevenue || 0), 0) / 100).toFixed(0)}
                </div>
                <div className="text-sm text-slate-600">Total Revenue</div>
              </div>
            </div>
          </div>
        )}
      </main>
    </PageBackground>
  );
}